import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { eur } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge } from "@/components/common";
import { Wrench, Car, Users, FileText, TrendingUp, Clock, Plus, AlertTriangle, CalendarClock, ScanSearch } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell } from "recharts";

function Kpi({ icon: Icon, label, value, tone = "text-primary", testid }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className={`h-5 w-5 ${tone}`} />
      </div>
      <div className="mt-3 font-heading text-3xl font-extrabold">{value}</div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-lg border border-border bg-popover px-3 py-2 text-xs shadow-xl">
      <div className="mb-1 font-bold">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-mono font-semibold">{p.dataKey === "revenue" ? eur(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [metric, setMetric] = useState("revenue");
  const [stock, setStock] = useState(null);
  const [credits, setCredits] = useState(null);
  const [myCredits, setMyCredits] = useState(null);
  const [attAlerts, setAttAlerts] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/warehouse/stock-chart").then((r) => setStock(r.data)).catch(() => {});
    api.get("/employees/attendance/alerts").then((r) => setAttAlerts((r.data?.rows || []).filter((x) => x.missing_out_days > 0))).catch(() => {});
    if (user?.role === "admin") {
      api.get("/spzapi/credits").then((r) => setCredits(r.data)).catch(() => {});
    } else {
      api.get("/billing/me").then((r) => setMyCredits(r.data)).catch(() => {});
    }
  }, [user]);

  const year = new Date().getFullYear();
  const monthly = d?.monthly || [];
  const reminders = d?.reminders || [];

  return (
    <div>
      <PageHeader title="Prehľad" subtitle="Rýchly pohľad na tvoj servis"
        action={
          user?.role === "admin"
            ? (credits?.configured ? (
                <div data-testid="spzapi-credits" className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${credits.credits != null && credits.credits < 3 ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-border bg-card text-muted-foreground"}`}>
                  <ScanSearch className="h-4 w-4" /> ŠPZ kredity (API): <span className="font-mono font-bold text-foreground">{credits.credits ?? "?"}</span>
                </div>
              ) : null)
            : (myCredits && !myCredits.unlimited && myCredits.spz_credits != null ? (
                <div data-testid="my-credits" className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm ${myCredits.spz_credits <= (myCredits.low_credit_threshold ?? 5) ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-border bg-card text-muted-foreground"}`}>
                  <ScanSearch className="h-4 w-4" /> Kredity: <span className="font-mono font-bold text-foreground">{myCredits.spz_credits}</span>
                </div>
              ) : null)
        } />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi icon={Wrench} label="Aktívne zákazky" value={d?.active_jobs ?? "–"} testid="kpi-active-jobs" />
        <Kpi icon={TrendingUp} label="Tržby tento mesiac" value={d ? eur(d.revenue_month) : "–"} tone="text-emerald-400" testid="kpi-revenue" />
        <Kpi icon={FileText} label="Nezaplatené faktúry" value={d?.unpaid_invoices ?? "–"} tone="text-amber-400" testid="kpi-unpaid" />
        <Kpi icon={Car} label="Vozidlá v evidencii" value={d?.total_vehicles ?? "–"} tone="text-blue-400" testid="kpi-vehicles" />
      </div>

      {attAlerts.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-4" data-testid="dashboard-missing-out">
          <AlertTriangle className="h-5 w-5 shrink-0 text-amber-500" />
          <div className="text-sm">
            <span className="font-semibold text-amber-500">Chýbajúce odchody tento mesiac: </span>
            <span className="text-foreground">{attAlerts.map((a) => `${a.name} (${a.missing_out_days} ${a.missing_out_days === 1 ? "deň" : a.missing_out_days < 5 ? "dni" : "dní"})`).join(", ")}</span>
          </div>
          <Button variant="outline" size="sm" className="ml-auto gap-2" onClick={() => navigate("/zamestnanci")} data-testid="dashboard-missing-out-link"><CalendarClock className="h-4 w-4" /> Opraviť dochádzku</Button>
        </div>
      )}

      {/* Chart */}
      <div className="card-surface mt-6 p-5" data-testid="dashboard-chart">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-bold uppercase">{metric === "revenue" ? `Tržby po mesiacoch · ${year}` : metric === "jobs" ? `Počet zákaziek · ${year}` : "Skladové zásoby (sledované)"}</h3>
            {metric === "revenue" && <p className="text-sm text-muted-foreground">Celkom za rok: <span className="font-mono font-semibold text-foreground">{eur(d?.revenue_year || 0)}</span></p>}
            {metric === "stock" && <p className="text-sm text-muted-foreground">Najnižší stav naprieč skladmi · <span className="text-red-400">červená = pod minimom</span></p>}
          </div>
          <div className="flex gap-1 rounded-lg border border-border bg-background/60 p-1">
            <button onClick={() => setMetric("revenue")} data-testid="chart-metric-revenue"
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${metric === "revenue" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Tržby</button>
            <button onClick={() => setMetric("jobs")} data-testid="chart-metric-jobs"
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${metric === "jobs" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Zákazky</button>
            <button onClick={() => setMetric("stock")} data-testid="chart-metric-stock"
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${metric === "stock" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>Skladové zásoby</button>
          </div>
        </div>
        {metric === "stock" && (!stock?.items?.length) ? (
          <div className="grid place-items-center py-16 text-center text-sm text-muted-foreground" data-testid="stock-chart-empty">
            Žiadne sledované položky. V sklade zapni pri položke „Sledovať stav zásob" a nastav minimum.
          </div>
        ) : (
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              {metric === "stock" ? (
                <BarChart data={stock?.items || []} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 28% 22%)" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: "hsl(215 18% 62%)", fontSize: 10 }} axisLine={false} tickLine={false} interval={0} angle={-20} textAnchor="end" height={60} />
                  <YAxis tick={{ fill: "hsl(215 18% 62%)", fontSize: 12 }} axisLine={false} tickLine={false} width={54} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(222 28% 22% / 0.4)" }} />
                  <Bar dataKey="qty" name="Na sklade" radius={[6, 6, 0, 0]} maxBarSize={38}>
                    {(stock?.items || []).map((it, i) => <Cell key={i} fill={it.below ? "#ef4444" : "hsl(var(--primary))"} />)}
                  </Bar>
                </BarChart>
              ) : (
                <BarChart data={monthly} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(222 28% 22%)" vertical={false} />
                  <XAxis dataKey="month" tick={{ fill: "hsl(215 18% 62%)", fontSize: 12 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: "hsl(215 18% 62%)", fontSize: 12 }} axisLine={false} tickLine={false} width={54} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "hsl(222 28% 22% / 0.4)" }} />
                  <Bar dataKey={metric} name={metric === "revenue" ? "Tržby" : "Zákazky"} radius={[6, 6, 0, 0]} maxBarSize={38}>
                    {monthly.map((_, i) => <Cell key={i} fill={metric === "revenue" ? "hsl(var(--primary))" : "#10b981"} />)}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        {/* Reminders */}
        <div className="card-surface p-5 lg:col-span-1" data-testid="reminders-panel">
          <button onClick={() => navigate("/pripomienky")} className="mb-4 flex w-full items-center justify-between gap-2 text-left transition-colors hover:text-primary" data-testid="reminders-open-page">
            <span className="flex items-center gap-2 font-heading text-lg font-bold uppercase"><CalendarClock className="h-5 w-5 text-amber-400" /> STK / Servis</span>
            <span className="text-xs font-semibold text-primary">Všetky →</span>
          </button>
          {reminders.length ? (
            <div className="space-y-2">
              {reminders.map((r, i) => (
                <div key={i} onClick={() => navigate("/vozidla")} data-testid={`reminder-${r.vehicle_id}-${r.type}`}
                  className={`flex cursor-pointer items-center justify-between rounded-lg border px-3 py-2 transition-colors hover:border-primary/50 ${r.overdue ? "border-red-500/40 bg-red-500/10" : "border-amber-500/30 bg-amber-500/10"}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-sm font-medium">
                      {r.overdue && <AlertTriangle className="h-3.5 w-3.5 text-red-400" />}
                      <span className="plate text-xs">{r.spz}</span>
                      <span className="text-xs text-muted-foreground">{r.type}</span>
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {r.date ? new Date(r.date).toLocaleDateString("sk-SK") : `${Number(r.current_km).toLocaleString("sk-SK")} / ${Number(r.km).toLocaleString("sk-SK")} km`}
                    </div>
                  </div>
                  <span className={`text-xs font-semibold ${r.overdue ? "text-red-400" : "text-amber-400"}`}>
                    {r.date ? (r.overdue ? `${Math.abs(r.days)} dní po` : `o ${r.days} dní`) : (r.overdue ? "prekročené" : "blíži sa")}
                  </span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">Žiadne termíny v najbližších 7 dňoch.</p>}
        </div>

        {/* Quick actions */}
        <div className="card-surface p-5 lg:col-span-1">
          <h3 className="mb-4 font-heading text-lg font-bold uppercase">Rýchle akcie</h3>
          <div className="flex flex-col gap-3">
            <Button className="min-h-[48px] justify-start gap-3" onClick={() => navigate("/zakazky")} data-testid="quick-new-job"><Plus className="h-5 w-5" /> Nová zákazka</Button>
            <Button variant="secondary" className="min-h-[48px] justify-start gap-3" onClick={() => navigate("/faktury")} data-testid="quick-new-invoice"><FileText className="h-5 w-5" /> Nová faktúra</Button>
            <Button variant="secondary" className="min-h-[48px] justify-start gap-3" onClick={() => navigate("/vozidla")} data-testid="quick-new-vehicle"><Car className="h-5 w-5" /> Pridať vozidlo</Button>
            <Button variant="secondary" className="min-h-[48px] justify-start gap-3" onClick={() => navigate("/zakaznici")} data-testid="quick-new-customer"><Users className="h-5 w-5" /> Pridať zákazníka</Button>
          </div>
        </div>

        {/* Recent jobs */}
        <div className="card-surface p-5 lg:col-span-1">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold uppercase"><Clock className="h-5 w-5 text-primary" /> Posledné zákazky</h3>
          {d?.recent_jobs?.length ? (
            <div className="divide-y divide-border">
              {d.recent_jobs.map((j) => (
                <div key={j.id} className="flex items-center justify-between py-3" data-testid={`recent-job-${j.id}`}>
                  <div className="min-w-0">
                    <div className="truncate font-medium">{j.title}</div>
                    <div className="text-xs text-muted-foreground">{new Date(j.created_at).toLocaleDateString("sk-SK")}</div>
                  </div>
                  <StatusBadge status={j.status} />
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-muted-foreground">Zatiaľ žiadne zákazky.</p>}
        </div>
      </div>
    </div>
  );
}
